import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Search, Shield, Zap, ChevronRight, Wrench, 
  Users, X 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

// --- IMPORT DA NAVBAR (OPÇÃO B) E CONTEXTO ---
import Navbar from '../components/ui/Navbar';
import { useAuth } from '../contexts/AuthContext'; 
import { db } from '../lib/firebase'; 
import { collection, getDocs, query } from 'firebase/firestore';

// --- ATIVOS ---
import logoSocorreAi from "../assets/logo-socorreai.png";
import planoFundo from "../assets/planodefundo.png"; 

const Landing = () => {
  const navigate = useNavigate();
  const { user, authLoading } = useAuth(); 
  
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const q = query(collection(db, 'functions')); 
        const querySnapshot = await getDocs(q);
        const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setCategories(data.sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      } catch (error) {
        console.error("Erro categorias:", error);
      }
    };
    fetchCategories();
  }, []);

  const handleServiceClick = (serviceName) => {
    if (user) {
      navigate('/dashboard', { state: { autoSearch: serviceName } });
    } else {
      navigate('/login', { state: { selectedCategory: serviceName } });
    }
  };

  const filteredCategories = categories.filter(cat => 
    (cat.name || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen flex flex-col font-sans text-slate-900 overflow-x-hidden">
      
      {/* 1. FUNDO FIXO OTIMIZADO */}
      <div 
        className="fixed inset-0 -z-10 w-full h-full pointer-events-none transform-gpu" 
        style={{ 
          backgroundImage: `url(${planoFundo})`, 
          backgroundSize: 'cover', 
          backgroundPosition: 'center', 
          backgroundRepeat: 'no-repeat' 
        }}
      >
        <div className="absolute inset-0 bg-slate-900/30" />
      </div>

      {/* 2. NAVBAR COMPONENTIZADA */}
      {!authLoading && (
        user ? (
          <Navbar />
        ) : (
          <header className="fixed top-0 left-0 right-0 z-[100] bg-white/95 border-b border-slate-200 py-4 px-6 md:px-8 shadow-md backdrop-blur-sm">
            <div className="max-w-7xl mx-auto flex justify-between items-center">
              <div className="flex items-center cursor-pointer" onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}>
                <img src={logoSocorreAi} alt="Logo" className="h-8 md:h-10" />
              </div>
              <div className="flex items-center gap-4">
                <button onClick={() => navigate('/register')} className="text-[10px] md:text-sm font-bold text-slate-600 hover:text-orange-500 transition-all">
                  Não possui conta? <span className="font-black text-orange-600 underline underline-offset-4">Cadastre-se</span>
                </button>
                <Button onClick={() => navigate('/login')} className="bg-orange-500 hover:bg-orange-600 text-white font-black text-xs px-5 py-5 rounded-xl shadow-lg uppercase">
                  Entrar
                </Button>
              </div>
            </div>
          </header>
        )
      )}

      <div className="relative z-10 pt-20">
        
        {/* HERO SECTION */}
        <section className="relative min-h-[80dvh] flex flex-col items-center justify-center text-center px-4 pt-10 pb-10">
          <motion.div 
            initial={{ opacity: 0, y: 10 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.5 }}
            className="w-full max-w-5xl will-change-transform"
          >
            <div className="relative inline-block bg-slate-50/95 p-8 md:p-12 rounded-full shadow-2xl mb-10 border-4 md:border-8 border-white/30 backdrop-blur-sm">
                <img src={logoSocorreAi} alt="Socorre Aí" className="mx-auto w-48 sm:w-80 lg:w-[480px]" />
            </div>
            
            <h1 className="text-xl sm:text-4xl lg:text-5xl font-black text-white uppercase italic tracking-tighter mb-10 drop-shadow-lg leading-tight px-2">
              O profissional certo para o seu socorro imediato.
            </h1>
          </motion.div>
        </section>

        {/* BUSCA E CATEGORIAS */}
        <section className="py-10 px-4 -mt-20 mb-20">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row gap-3 mb-8 items-stretch transform-gpu">
                <div className="relative flex-1">
                    <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input 
                        placeholder="Qual profissional você procura hoje?"
                        className="h-16 md:h-20 pl-14 pr-6 text-base md:text-xl rounded-2xl md:rounded-3xl border-none bg-white shadow-xl font-bold focus-visible:ring-2 focus-visible:ring-blue-500"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button onClick={() => setSearchTerm("")} className="absolute right-6 top-1/2 -translate-y-1/2 p-2 bg-slate-100 rounded-full">
                            <X className="w-4 h-4 text-slate-400" />
                        </button>
                    )}
                </div>
                <Button 
                    onClick={() => searchTerm && handleServiceClick(searchTerm)}
                    className="bg-orange-500 hover:bg-orange-600 h-16 md:h-20 px-8 rounded-2xl md:rounded-3xl text-white font-black text-sm md:text-xl shadow-lg uppercase active:scale-95 transition-transform"
                >
                    Solicitar
                </Button>
            </div>

            <div className="bg-white/95 backdrop-blur-sm rounded-[2rem] shadow-xl border border-white/50 overflow-hidden">
              <div className="p-5 border-b border-slate-100 bg-slate-50/50 flex items-center gap-3 font-black text-slate-400 text-[10px] uppercase tracking-widest">
                Categorias Disponíveis:
              </div>
              <div className="max-h-[400px] overflow-y-auto custom-scrollbar p-4 md:p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {filteredCategories.map((service) => (
                      <div 
                        key={service.id} 
                        onClick={() => handleServiceClick(service.name)} 
                        className="flex items-center justify-between p-5 bg-slate-50 rounded-xl border border-slate-100 cursor-pointer active:bg-blue-50 transition-colors group"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center border shadow-sm text-slate-400 group-hover:text-blue-600 transition-colors">
                            <Wrench className="w-5 h-5" />
                          </div>
                          <span className="font-black text-slate-700 uppercase italic text-sm tracking-tighter group-hover:text-blue-700 transition-colors">
                            {service.name}
                          </span>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-200 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* COMO FUNCIONA */}
        <section className="py-20 px-6 bg-white/10 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto text-center">
            <h2 className="text-2xl md:text-5xl font-black mb-16 uppercase tracking-tighter italic text-slate-900">Como Funciona</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { step: '1', title: 'O Problema', desc: 'Descreva o que você precisa em poucos segundos.' },
                { step: '2', title: 'A Escolha', desc: 'Analise os perfis e escolha o melhor técnico.' },
                { step: '3', title: 'A Solução', desc: 'Resolva seu problema com rapidez e garantia.' },
              ].map((item, idx) => (
                <div key={idx} className="bg-white/80 rounded-3xl p-8 border border-white/50 shadow-lg text-center flex flex-col items-center transform-gpu">
                  <div className="w-14 h-14 bg-blue-600 text-white rounded-xl flex items-center justify-center text-2xl font-black mb-6 rotate-3">{item.step}</div>
                  <h3 className="text-xl font-black uppercase italic mb-3 text-slate-800 tracking-tighter">{item.title}</h3>
                  <p className="text-slate-600 text-sm font-medium leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* POR QUE O SOCORRE AI */}
        <section className="py-20 text-white bg-slate-900/95 px-6 border-y border-white/5">
          <div className="max-w-7xl mx-auto text-center">
            <h2 className="text-2xl md:text-5xl font-black mb-16 uppercase tracking-tighter italic text-white">Por Que o Socorre Aí?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              {[
                { icon: Shield, title: 'Confiança', desc: 'Profissionais verificados rigorosamente para sua segurança.' },
                { icon: Zap, title: 'Agilidade', desc: 'Suporte técnico encontrado em questão de minutos.' },
                { icon: Users, title: 'Comunidade', desc: 'Melhores escolhas baseadas em avaliações reais.' },
              ].map((feature, idx) => (
                <div key={idx}>
                  <div className="w-16 h-16 mx-auto mb-6 bg-white/5 rounded-2xl flex items-center justify-center">
                    <feature.icon className="w-8 h-8 text-orange-500" />
                  </div>
                  <h3 className="text-xl font-black uppercase italic mb-4 tracking-tighter">{feature.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed px-4">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <footer className="py-12 text-center bg-slate-950 text-slate-600 text-[9px] font-black uppercase tracking-[0.3em] px-4">
          &copy; {new Date().getFullYear()} SOCORRE AÍ - TECNOLOGIA EM SERVIÇOS
        </footer>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
        * { -webkit-tap-highlight-color: transparent; }
      `}} />
    </div>
  );
};

export default Landing;